import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Camera } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const ax = { withCredentials: true };
const MAX_PHOTOS = 5;

const newKey = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const whole = (v) => (/^\d+$/.test(String(v).trim()) ? parseInt(v, 10) : NaN);
const errorText = (e, t) => {
  const detail = e.response?.data?.detail;
  return typeof detail === "string" ? detail : t("inb.error");
};

/** Uploads photos one by one after the receipt exists; returns how many failed. */
export async function uploadPhotos(receiptId, files) {
  let failed = 0;
  for (const file of files) {
    const form = new FormData();
    form.append("file", file);
    try { await axios.post(`${API}/inbound/receipts/${receiptId}/photos`, form, ax); } catch { failed += 1; }
  }
  return failed;
}

function PhotoPicker({ files, onChange, max = MAX_PHOTOS }) {
  const { t } = useLang();
  const urls = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach(u => URL.revokeObjectURL(u)), [urls]);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {files.length < max && (
        <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold hover:bg-slate-50">
          <Camera className="h-4 w-4" /> {t("recv.photo_add")}
          <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="hidden"
            onChange={e => { onChange([...files, ...Array.from(e.target.files || [])].slice(0, max)); e.target.value = ""; }} />
        </label>
      )}
      {urls.map((u, i) => (
        <button key={u} type="button" title={t("inb.photo_remove")} onClick={() => onChange(files.filter((_, j) => j !== i))}>
          <img src={u} alt="" className="h-11 w-11 rounded-md border border-slate-200 object-cover" />
        </button>
      ))}
      {files.length > 0 && <span className="text-xs text-slate-400">{files.length}/{max}</span>}
    </div>
  );
}

// "Damaged" is opt-in so the normal receipt stays one number + Enter.
function DamageFields({ value, onChange, total, withNote = true }) {
  const { t } = useLang();
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3">
      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm font-semibold">
        <Checkbox checked={value.on} onCheckedChange={on => set({ on: !!on })} data-testid="inbound-damage-toggle" />
        {t("inb.damaged_toggle")}
      </label>
      {value.on && (
        <div className="space-y-3 border-l-2 border-red-200 pl-3">
          <div className="space-y-1.5">
            <Label>{t("inb.damaged_of_total")}</Label>
            <Input type="number" min={1} max={total || undefined} inputMode="numeric" value={value.damaged} autoFocus
              onChange={e => set({ damaged: e.target.value })} className="h-10" />
          </div>
          {withNote && (
            <div className="space-y-1.5">
              <Label>{t("common.notes")}</Label>
              <Input value={value.note} maxLength={500} placeholder={t("inb.damage_note_placeholder")}
                onChange={e => set({ note: e.target.value })} className="h-10" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("inb.photos")}</Label>
            <PhotoPicker files={value.files} onChange={files => set({ files })} />
          </div>
        </div>
      )}
    </div>
  );
}

const NO_DAMAGE = { on: false, damaged: "", note: "", files: [] };

function damageError(dmg, quantity, t) {
  if (!dmg.on) return "";
  const d = whole(dmg.damaged);
  if (!(d >= 1)) return t("inb.damaged_required");
  return d > quantity ? t("inb.damaged_too_many") : "";
}

/** Sender: one delivery against an order row. */
export function ReceiveDialog({ row, onClose, onSaved }) {
  const { t } = useLang();
  const [qty, setQty] = useState(String(row.remaining || ""));
  const [dmg, setDmg] = useState(NO_DAMAGE);
  const [clientKey] = useState(newKey);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const n = whole(qty);
  const d = dmg.on ? whole(dmg.damaged) || 0 : 0;
  const left = row.ordered_quantity - row.received - (n || 0);

  const save = async (e) => {
    e.preventDefault();
    const err = !(n >= 1) ? t("inb.quantity_required") : damageError(dmg, n, t);
    setError(err);
    if (err) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/inbound/items/${row.id}/receipts`,
        { quantity: n, damaged: d, note: dmg.on ? dmg.note.trim() || null : null, client_key: clientKey }, ax);
      onSaved(data, dmg.on ? dmg.files : []);
    } catch (e2) { setError(errorText(e2, t)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("inb.receive")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{row.product_name || row.asin}</span>
            {row.tenant_name && <> · {row.tenant_name}</>}<br />
            {t("inb.receive_summary", { ordered: row.ordered_quantity, received: row.received, remaining: row.remaining })}
          </p>
          <div className="space-y-1.5">
            <Label>{t("inb.received_now")}</Label>
            <Input type="number" min={1} inputMode="numeric" value={qty} autoFocus onFocus={e => e.target.select()}
              onChange={e => setQty(e.target.value)} className="h-11 text-lg" data-testid="inbound-receive-qty" />
          </div>
          <DamageFields value={dmg} onChange={setDmg} total={n} />
          {n >= 1 && (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              {d > 0 ? t("inb.preview_split", { count: n, good: n - d, damaged: d }) : t("inb.preview", { count: n })}{" → "}
              {left > 0 ? <b className="text-amber-700">{t("inb.preview_left", { count: left })}</b>
                : left === 0 ? <b className="text-emerald-700">{t("inb.preview_complete")}</b>
                  : <b className="text-red-600">{t("inb.preview_over", { count: -left })}</b>}
            </p>
          )}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="inbound-receive-save">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function useTenantProducts(tenantId) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (tenantId === null) return;
    const params = tenantId ? { tenant_id: tenantId } : {};
    axios.get(`${API}/products`, { ...ax, params }).then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, [tenantId]);
  return products;
}

/** Sender: goods that came without a row. The owner comes from the parcel; "owner unknown" keeps it out of all stock. */
export function UnannouncedDialog({ warehouse, onClose, onSaved }) {
  const { t } = useLang();
  const [tenantId, setTenantId] = useState("");
  const [unknown, setUnknown] = useState(false);
  const [asin, setAsin] = useState("");
  const [qty, setQty] = useState("");
  const [orderId, setOrderId] = useState("");
  const [note, setNote] = useState("");
  const [dmg, setDmg] = useState(NO_DAMAGE);
  const [clientKey] = useState(newKey);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const products = useTenantProducts(tenantId && !unknown ? tenantId : null);
  const code = asin.trim().toUpperCase();
  const notInList = tenantId && !unknown && code.length >= 10 && !products.some(p => p.asin.toUpperCase() === code);
  const n = whole(qty);

  const save = async (e) => {
    e.preventDefault();
    const err = !unknown && !tenantId ? t("inb.tenant_required") : !code ? t("inb.asin_required")
      : !(n >= 1) ? t("inb.quantity_required") : damageError(dmg, n, t);
    setError(err);
    if (err) return;
    setSaving(true);
    const match = products.find(p => p.asin.toUpperCase() === code);
    try {
      const { data } = await axios.post(`${API}/inbound/unannounced`, {
        warehouse_id: warehouse.id, tenant_id: unknown ? null : tenantId, asin: match ? match.asin : code,
        quantity: n, damaged: dmg.on ? whole(dmg.damaged) : 0, note: note.trim() || null,
        order_id: orderId.trim() || null, client_key: clientKey }, ax);
      onSaved(data.receipt, dmg.files, unknown);
    } catch (e2) { setError(errorText(e2, t)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("inb.unannounced")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <p className="text-sm text-slate-600">{t("inb.unannounced_hint")}</p>
          <div className="space-y-1.5">
            <Label>{t("planning.tenant")}</Label>
            <Select value={tenantId} onValueChange={setTenantId} disabled={unknown}>
              <SelectTrigger className="h-10"><SelectValue placeholder={t("inb.select_tenant")} /></SelectTrigger>
              <SelectContent>
                {warehouse.tenants.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex w-fit cursor-pointer items-center gap-2 pt-1 text-sm font-semibold">
              <Checkbox checked={unknown} onCheckedChange={v => { setUnknown(!!v); if (v) setTenantId(""); }} />
              {t("inb.owner_unknown")}
            </label>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.asin")}</Label>
            <Input value={asin} onChange={e => setAsin(e.target.value)} list="inbound-unannounced-products"
              autoComplete="off" className="h-10 uppercase" maxLength={50} />
            <datalist id="inbound-unannounced-products">
              {products.map(p => <option key={p.asin} value={p.asin}>{p.product_name}</option>)}
            </datalist>
            {unknown && <p className="text-xs text-slate-500">{t("inb.owner_unknown_hint")}</p>}
            {notInList && <p className="text-xs text-amber-700">{t("inb.not_in_list_hint")}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("inb.received_now")}</Label>
              <Input type="number" min={1} inputMode="numeric" value={qty} onChange={e => setQty(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("inb.order_id_optional")}</Label>
              <Input value={orderId} maxLength={100} onChange={e => setOrderId(e.target.value)} className="h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.notes")}</Label>
            <Input value={note} maxLength={500} placeholder={t("inb.unannounced_note_placeholder")}
              onChange={e => setNote(e.target.value)} className="h-10" />
          </div>
          <DamageFields value={dmg} onChange={setDmg} total={n} withNote={false} />
          {!dmg.on && (
            <div className="space-y-1.5">
              <Label>{t("inb.photos")}</Label>
              <PhotoPicker files={dmg.files} onChange={files => setDmg({ ...dmg, files })} />
            </div>
          )}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Tenant: open a new order row, or fix the ordered quantity of an existing one. */
export function ItemDialog({ row, warehouseId, onClose, onSaved }) {
  const { t } = useLang();
  const editing = !!row;
  const [asin, setAsin] = useState("");
  const [qty, setQty] = useState(editing ? String(row.ordered_quantity) : "");
  const [orderId, setOrderId] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const products = useTenantProducts(editing ? null : "");

  const save = async (e) => {
    e.preventDefault();
    const n = whole(qty);
    const err = !editing && !asin.trim() ? t("inb.asin_required") : !(n >= 1) ? t("inb.quantity_required")
      : !editing && !orderId.trim() ? t("inb.order_id_required") : "";
    setError(err);
    if (err) return;
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/inbound/items/${row.id}`, { ordered_quantity: n }, ax);
      else await axios.post(`${API}/inbound/items`, { warehouse_id: warehouseId, asin: asin.trim(), ordered_quantity: n,
                                                      order_id: orderId.trim() }, ax);
      onSaved();
    } catch (e2) { setError(errorText(e2, t)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? t("inb.edit_quantity") : t("inb.add")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {editing ? (
            <p className="text-sm text-slate-600">{row.product_name || row.asin} · {row.order_id}</p>
          ) : (
            <>
              <p className="text-sm text-slate-600">{t("inb.add_hint")}</p>
              <div className="space-y-1.5">
                <Label>{t("inb.product_approved")}</Label>
                <Input value={asin} onChange={e => setAsin(e.target.value)} list="inbound-tenant-products" autoComplete="off"
                  placeholder={t("planning.item_search")} className="h-10" data-testid="inbound-add-asin" />
                <datalist id="inbound-tenant-products">
                  {products.map(p => <option key={p.asin} value={p.asin}>{p.product_name}</option>)}
                </datalist>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("inb.ordered")}</Label>
              <Input type="number" min={1} inputMode="numeric" value={qty} onChange={e => setQty(e.target.value)} className="h-10" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>{t("inb.order_id")}</Label>
                <Input value={orderId} maxLength={100} placeholder="114-3200711-0856215" onChange={e => setOrderId(e.target.value)} className="h-10" />
              </div>
            )}
          </div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Sender: give an owner-unknown row its tenant. */
export function AssignDialog({ row, tenants, onClose, onSaved }) {
  const { t } = useLang();
  const [tenantId, setTenantId] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    try {
      await axios.post(`${API}/inbound/items/${row.id}/assign`, { tenant_id: tenantId }, ax);
      onSaved();
    } catch (e) { setError(errorText(e, t)); }
  };
  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{t("inb.assign")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{row.asin} · {t("inb.units", { count: row.received })}</p>
        <Select value={tenantId} onValueChange={setTenantId}>
          <SelectTrigger className="h-10"><SelectValue placeholder={t("inb.select_tenant")} /></SelectTrigger>
          <SelectContent>{tenants.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
        </Select>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={!tenantId} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Every delivery of one row with notes and photos; the tenant acknowledges damage, the sender adds photos later. */
export function ReceiptsDialog({ row, side, onClose, onChanged }) {
  const { t } = useLang();
  const sender = side === "sender";
  const [receipts, setReceipts] = useState([]);
  const [error, setError] = useState("");
  const load = () => axios.get(`${API}/inbound/items/${row.id}/receipts`, ax)
    .then(({ data }) => setReceipts(data)).catch(e => setError(errorText(e, t)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [row.id]);

  const addPhotos = async (rid, files) => {
    const failed = await uploadPhotos(rid, files);
    setError(failed ? t("recv.photo_error") : "");
    load();
  };
  const acknowledge = async () => {
    try { await axios.post(`${API}/inbound/items/${row.id}/acknowledge`, {}, ax); onChanged(); }
    catch (e) { setError(errorText(e, t)); }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("inb.receipts")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{row.product_name || row.asin}{row.order_id && <> · {row.order_id}</>}</p>
        <div className="space-y-2">
          {receipts.map(r => (
            <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex flex-wrap justify-between gap-2">
                <b>
                  {t("inb.units", { count: r.good + r.damaged })}
                  {r.damaged > 0 && <span className="text-red-600"> · {t("inb.damaged_count", { count: r.damaged })}</span>}
                </b>
                <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              {r.note && <p className="mt-1 text-slate-700">{r.note}</p>}
              {r.photo_paths.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.photo_paths.map(url => (
                    <a key={url} href={`${BACKEND}${url}`} target="_blank" rel="noreferrer">
                      <img src={`${BACKEND}${url}`} alt={t("inb.photos")} className="h-14 w-14 rounded-lg border border-slate-200 object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {sender && r.photo_paths.length < MAX_PHOTOS && (
                <div className="mt-2">
                  <PhotoPicker files={[]} max={MAX_PHOTOS - r.photo_paths.length} onChange={files => addPhotos(r.id, files)} />
                </div>
              )}
            </div>
          ))}
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.close")}</Button>
          {side === "tenant" && row.unacknowledged_damage && (
            <Button onClick={acknowledge} className="bg-blue-600 hover:bg-blue-700">{t("inb.acknowledge")}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
